import { Navigate } from 'react-router-dom';
import { useContext, useEffect, useRef } from 'react';
import { UserContext } from '../context/UserContext';

/* This component is used to protect URLs from unauthorized access. It is used in App.js. 
   Uses cached authentication from UserContext to avoid API calls on every navigation.
   Token verification is only done once on initial auth state change in UserContext. */

// Cache for token verification to avoid repeated API calls
const tokenVerificationCache = {
    verified: false,
    userId: null,
    timestamp: null,
    CACHE_DURATION: 5 * 60 * 1000 // 5 minutes
};

const ProtectedRoute = ({ children }) => {
    const { user, loading } = useContext(UserContext);
    const verificationInProgress = useRef(false);

    // Check if user is authenticated and email is verified
    const isAuthenticated = user && user.emailVerified;

    // Optional: Verify token in background (non-blocking) for security
    // This runs once per session, not on every route change
    useEffect(() => {
        const verifyTokenInBackground = async () => {
            if (!user || !user.emailVerified || verificationInProgress.current) return;
            
            // Check cache - skip if recently verified for same user
            const now = Date.now();
            if (
                tokenVerificationCache.verified && 
                tokenVerificationCache.userId === user.uid &&
                tokenVerificationCache.timestamp &&
                (now - tokenVerificationCache.timestamp) < tokenVerificationCache.CACHE_DURATION
            ) {
                return; // Skip - recently verified
            }

            verificationInProgress.current = true;
            
            try {
                const API_BASE_URL = window.location.hostname === 'localhost'
                    ? 'http://localhost:5001'
                    : 'https://us-central1-joynet-99b18.cloudfunctions.net/app';

                const token = await user.getIdToken();
                const response = await fetch(`${API_BASE_URL}/api/verify-token`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    credentials: 'include'
                });

                // Update cache
                tokenVerificationCache.verified = response.ok;
                tokenVerificationCache.userId = user.uid;
                tokenVerificationCache.timestamp = now;

                // If token is invalid, could trigger a sign-out here if needed
                if (!response.ok) {
                    console.warn('Token verification failed in background');
                }
            } catch (error) {
                console.error('Background token verification error:', error);
            } finally {
                verificationInProgress.current = false;
            }
        };

        verifyTokenInBackground();
    }, [user]);

    // Show loading state while checking auth
    if (loading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                backgroundColor: '#F6F6F9'
            }}>
                <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #E9505F',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    // Redirect to login if not authenticated
    return isAuthenticated ? children : <Navigate to="/login" />;
};

export default ProtectedRoute;